import {
  BasicInsuranceModel,
  BasicInsuranceModelType,
  BasicInsurancePlan,
  BasicInsurancePlanType,
  ContractDuration,
  GenderOrPrenatal,
} from '../../../api';
import { Item } from './tracking-ecommerce.events';
import {
  CATEGORY,
  genderNames,
  mapAccidentCoverage,
  mapBrand,
  mapContractLength,
  mapCustomerType,
  mapExistingCustomer,
  mapFranchise,
} from './tracking-ecommerce.map';
import { PersonBasicInsuranceState } from '../../../states/offer/offer.state';
import { priceIfPresentOrZero } from './tracking-ecommerce.utils';

const basicInsurancePlanNames: {
  [key in BasicInsurancePlanType]: string;
} = {
  GENERAL_PRACTITIONER_PLAN: 'Hausarztmodelle',
  TELEPHONE_PLAN: 'Telemedizinische Modelle',
  COMBINATION_PLAN: 'Kombiniertes Modell: Hausarzt und Telemedizin',
  TRADITIONAL_PLAN: 'Traditionelles Modell: Freie Arztwahl',
  INTEGRATED_CARE_PLAN: 'Viva',
};

const basicInsuranceModelIds: {
  [key in BasicInsuranceModelType]: string;
} = {
  TRADITIONAL: 'traditional',
  MANAGED_CARE: 'managed_care',
  MED_DIRECT: 'med_direct',
  HMO_PLUS: 'hmo_plus',
  CASA_NOVA: 'casa_nova',
  TEL_DOC: 'tel_doc',
  MED_CALL: 'med_call',
  TEL_CARE: 'tel_care',
  COMBI_CARE: 'combi_care',
  VIVA: 'viva',
};

const basicInsuranceModelNames: {
  [key in BasicInsuranceModelType]: string;
} = {
  TRADITIONAL: 'Traditionelles Modell: Freie Arztwahl',
  MANAGED_CARE: 'Hausarztmodelle',
  MED_DIRECT: 'Hausarztmodelle',
  HMO_PLUS: 'HMO plus',
  CASA_NOVA: 'Hausarztmodelle',
  TEL_DOC: 'Telemedizinische Modelle',
  MED_CALL: 'Telemedizinische Modelle',
  TEL_CARE: 'Telemedizinische Modelle',
  COMBI_CARE: 'Kombiniertes Modell: Hausarzt und Telemedizin',
  VIVA: 'Viva',
};

export function mapBasicInsuranceItem(
  basicInsurancePlan: BasicInsurancePlan,
  franchise: string,
  includesAccidentCoverage: boolean,
  gender: GenderOrPrenatal,
): Item {
  return {
    item_name: basicInsurancePlanNames[basicInsurancePlan.type],
    item_category: CATEGORY.BASIC_INSURANCE,
    item_category2: mapFranchise(franchise),
    item_category3: genderNames[gender],
    price: basicInsurancePlan.lowest_price,
    quantity: 1,
    item_variant: mapAccidentCoverage(includesAccidentCoverage),
  };
}

export function mapBasicInsuranceItemByModel(
  basicInsuranceModel: BasicInsuranceModel,
  franchise: string,
  includesAccidentCoverage: boolean,
  gender: GenderOrPrenatal,
): Item {
  return {
    item_id: basicInsuranceModelIds[basicInsuranceModel.type],
    item_name: basicInsuranceModelNames[basicInsuranceModel.type],
    item_category: CATEGORY.BASIC_INSURANCE,
    item_category2: mapFranchise(franchise),
    item_category3: genderNames[gender],
    price: basicInsuranceModel.price,
    quantity: 1,
    item_variant: mapAccidentCoverage(includesAccidentCoverage),
  };
}

export function mapBasicInsuranceDetailItemFromModel(
  basicInsuranceModel: BasicInsuranceModel,
  franchise: string,
  includesAccidentCoverage: boolean,
  gender: GenderOrPrenatal,
): Item {
  return {
    item_id: basicInsuranceModelIds[basicInsuranceModel.type],
    item_name: basicInsuranceModelNames[basicInsuranceModel.type],
    item_category: CATEGORY.BASIC_INSURANCE,
    item_category2: mapFranchise(franchise),
    item_category3: genderNames[gender],
    price: basicInsuranceModel.price,
    quantity: 1,
    item_variant: mapAccidentCoverage(includesAccidentCoverage),
  };
}

export function mapBasicInsuranceDetailItemFromState(
  basicInsurance: PersonBasicInsuranceState,
  totalPersons: number,
  gender: GenderOrPrenatal,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
): Item {
  return {
    item_id: basicInsuranceModelIds[basicInsurance.key.modelType],
    item_name: basicInsuranceModelNames[basicInsurance.key.modelType],
    item_category: CATEGORY.BASIC_INSURANCE,
    item_category2: mapFranchise(basicInsurance.franchise),
    item_category3: genderNames[gender],
    item_category4: mapCustomerType(totalPersons),
    item_category5: existingCustomer !== undefined ? mapExistingCustomer(existingCustomer) : undefined,
    item_brand: existingCustomer !== undefined ? mapBrand(basicInsurance.key.insurer) : undefined,
    price: priceIfPresentOrZero(basicInsurance.price),
    quantity: 1,
    item_variant: mapAccidentCoverage(basicInsurance.includesAccidentCoverage),
    contract_length:
      contractDuration && existingCustomer !== undefined ? mapContractLength(contractDuration) : undefined,
  };
}
